/**
 * TravelOperatorNew — /travel/operators/new
 * Anyone signed-in can submit a new operator. Admin submissions go
 * live instantly; others land as `pending`.
 */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plane } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import TravelCategoryNav from "@/components/TravelCategoryNav";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { key: "airline",       label: "Airline" },
  { key: "airport",       label: "Airport" },
  { key: "rail_operator", label: "Rail Operator" },
  { key: "rail_station",  label: "Rail Station" },
  { key: "bus_terminal",  label: "Bus Terminal" },
  { key: "boat_terminal", label: "Boat / Ferry Terminal" },
  { key: "seaport",       label: "Seaport" },
  { key: "taxi_service",  label: "Taxi / Rideshare" },
];

const TYPE_TO_SLUG = {
  airline: "airlines", airport: "airports", rail_operator: "rail",
  rail_station: "rail-stations", bus_terminal: "bus",
  boat_terminal: "cruises", seaport: "seaports", taxi_service: "taxi",
};

export default function TravelOperatorNew() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    type: "airline",
    name: "", code: "", country: "", state: "", city: "",
    website: "", photo_url: "", description: "",
  });

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Please sign in first."); return; }
    if (!f.name.trim()) { toast.error("Name required."); return; }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/api/travel/operators`,
        { ...f, name: f.name.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const op = r.data.operator;
      if (op.status === "active") {
        toast.success("Operator added!");
        navigate(`/travel/${TYPE_TO_SLUG[op.type]}/${op.slug}`);
      } else {
        toast.success("Submitted for review — an admin will publish it shortly.");
        navigate(`/travel`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-3xl mb-2">Sign in to add a travel operator</h1>
        <Link to="/auth" className="wofy-btn-primary inline-block mt-3">Sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="travel-operator-new">
      <TravelCategoryNav />
      <Link to="/travel" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-4 mt-4">
        <ArrowLeft className="w-4 h-4" /> Travel
      </Link>
      <h1 className="font-display text-3xl mb-1">Add a travel operator</h1>
      <p className="text-sm opacity-70 mb-6">
        Airlines, airports, rail, bus, ferry & seaports. Non-admin submissions are reviewed before going live.
      </p>

      <form onSubmit={submit} className="wofy-card !p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Type *</label>
          <select value={f.type} onChange={set("type")} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="type-select">
            {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-1">Name *</label>
            <input required value={f.name} onChange={set("name")} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} placeholder="e.g. Emirates" data-testid="name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Code (IATA / ICAO / UIC)</label>
            <input value={f.code} onChange={set("code")} className="w-full px-3 py-2 rounded-lg border font-mono" style={{ borderColor: "var(--wofy-border)" }} placeholder="EK" maxLength={12} data-testid="code-input" />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <input value={f.country} onChange={set("country")} placeholder="Country" className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="country-input" />
          <input value={f.state}   onChange={set("state")}   placeholder="State/Region" className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="state-input" />
          <input value={f.city}    onChange={set("city")}    placeholder="City" className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="city-input" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Website</label>
          <input value={f.website} onChange={set("website")} placeholder="https://…" className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="website-input" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Photo URL</label>
          <input value={f.photo_url} onChange={set("photo_url")} placeholder="https://…" className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="photo-input" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Short description</label>
          <textarea value={f.description} onChange={set("description")} rows={4} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} placeholder="2-3 sentences travelers will find useful." maxLength={8000} data-testid="desc-input" />
        </div>

        <div className="pt-2 flex items-center justify-between">
          <div className="text-xs opacity-60">
            {user?.role === "admin" ? "Admin — publishes instantly." : "Your submission will be reviewed."}
          </div>
          <button type="submit" disabled={saving} className="wofy-btn-primary inline-flex items-center gap-2" data-testid="submit-operator-btn">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</> : <><Plane className="w-4 h-4" />Submit</>}
          </button>
        </div>
      </form>
    </div>
  );
}
